"use client";

import Image from "next/image";
import CustomModal from "@/components/shared/CustomModal";
import { ActionButton } from "@/components/shared/ActionButton";
import type { IntegrationLinkKey } from "@/utils/constants/services/serviceCatalog";
import IntegrationPartnerLoadingDots from "./IntegrationPartnerLoadingDots";
import { Typography } from "@/components/Typography";

type IntegrationPartnerModalProps = {
  open: boolean;
  integrationKey: IntegrationLinkKey | null;
  state: "loading" | "error";
  onClose: () => void;
  onRetry: () => void;
};

const PARTNER_LOGOS: Record<IntegrationLinkKey, string> = {
  bookmefly: "/integration-partner/bookmefly.svg",
  knowroaming: "/integration-partner/knowroaming.svg",
};

const IntegrationPartnerModal = ({
  open,
  integrationKey,
  state,
  onClose,
  onRetry,
}: IntegrationPartnerModalProps) => {
  if (!integrationKey) {
    return null;
  }

  const isLoading = state === "loading";

  return (
    <CustomModal
      open={open}
      onOpenChange={onClose}
      title={isLoading ? "Loading partner app" : "Partner app error"}
      hideHeader
      hideDivider
      contentClassName="w-[min(100vw-32px,500px)] rounded-2xl pb-0"
      bodyClassName="overflow-y-visible pb-0"
    >
      <div className="min-h-[400px] px-6 py-6 md:min-h-[300px]">
        {isLoading ? (
          <div className="flex min-h-[352px] flex-col items-center justify-center gap-[10px] text-center md:min-h-[352px]">
            <div className="flex items-center justify-center gap-5">
              <Image
                src="/integration-partner/tovtrip.svg"
                alt="Tovtrip"
                width={64}
                height={64}
                className="size-16"
              />
              <IntegrationPartnerLoadingDots />
              <Image
                src={PARTNER_LOGOS[integrationKey]}
                alt="Partner"
                width={64}
                height={64}
                className="size-16"
              />
            </div>

            <Typography variant="h4" className="mt-4">
              Redirecting to our partner&apos;s website
            </Typography>

            <Typography variant="body1" className="font-normal">
              You&apos;ll be seamlessly redirected from Tovtrip to our partner&apos;s
              website to continue your experience.
            </Typography>
          </div>
        ) : (
          <div className="flex min-h-[300px] flex-col items-center justify-center gap-[10px] text-center md:min-h-[300px]">
            <Image
              src="/integration-partner/man-with-suitcase.svg"
              alt="Travel illustration"
              width={196}
              height={160}
              className="h-auto w-[180px] sm:w-[196px]"
            />

            <h3 className="mt-2 text-[28px] leading-[34px] font-semibold tracking-[-0.02em] text-[#303030]">
              Just a moment...
            </h3>

            <p className="max-w-[360px] text-[20px] leading-[28px] text-[#6B7280]">
              You can refresh the page if it doesn&apos;t appear shortly.
            </p>

            <div className="mt-4 grid w-full max-w-[368px] gap-3 sm:grid-cols-2">
              <ActionButton
                variant="secondary"
                className="h-14 text-[16px]"
                onClick={onClose}
              >
                Back to Tovtrip
              </ActionButton>
              <ActionButton
                onClick={onRetry}
                className="h-14 text-[16px] font-semibold"
              >
                Refresh
              </ActionButton>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
};

export default IntegrationPartnerModal;
